import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { GraduationCap, X } from "lucide-react";

const fmt = (v) => (v === null || v === undefined ? "—" : v);
const weak = (v) => typeof v === "number" && v < 50;
const subjColor = () => "#16324F";

function Circle({ label, value, max, color }) {
  const num = typeof value === "number" ? value : 0;
  const pct = max ? Math.min(1, Math.max(0, num / max)) : 0;
  const R = 33;
  const C = 2 * Math.PI * R;
  return (
    <div className="flex min-w-0 flex-col items-center gap-1.5">
      <div className="relative h-[92px] w-[92px] print:h-[66px] print:w-[66px]">
        <svg viewBox="0 0 84 84" className="h-full w-full -rotate-90">
          <circle cx="42" cy="42" r={R} fill="none" stroke="#E8EDF3" strokeWidth="7" />
          <circle cx="42" cy="42" r={R} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct)} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-xl font-extrabold print:text-base" style={{ color }}>{fmt(value)}</div>
      </div>
      <div className="max-w-[96px] text-center text-[11px] font-bold leading-tight text-slate-500 print:text-[9px]">{label}</div>
    </div>
  );
}

function ExtraInput({ subjectName, onAdd }) {
  const [v, setV] = useState("");
  const submit = () => { if (v.trim()) { onAdd(v.trim()); setV(""); } };
  return (
    <span className="flex items-center gap-1.5">
      <input data-testid={`extra-kz-input-${subjectName}`} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} placeholder="Elle kazanım ekle…" className="flex-1 rounded border border-slate-200 px-1.5 py-1 text-[11px] outline-none focus:border-slate-400" />
      <button data-testid={`extra-kz-add-${subjectName}`} onClick={submit} className="rounded bg-[#16324F] px-2.5 py-1 text-[10px] font-bold text-white transition hover:bg-[#0F2440] active:scale-95">Ekle</button>
    </span>
  );
}

export default function SonucBelgesi({ d }) {
  return (
    <div className="text-slate-800">
      {/* Başlık bandı */}
      <div className="flex items-center justify-between gap-4 rounded-t-2xl bg-[#16324F] px-5 py-4 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10"><GraduationCap className="h-7 w-7 text-white" /></div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300">ZOR NEW GENERATION ACADEMY</p>
            <p className="font-display text-lg font-extrabold leading-tight">{d.docTitle}</p>
            <p className="text-[11px] text-slate-300">{d.examName}{d.subInfo ? ` · ${d.subInfo}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-right">
          <div>
            <p className="font-display text-xl font-extrabold leading-tight">{d.student?.name}</p>
            <p className="text-[11px] text-slate-300">{d.student?.grade ? `${d.student.grade}. SINIF` : ""}{d.student?.section ? ` / ${d.student.section}` : ""}</p>
          </div>
          <div className="shrink-0 rounded-lg bg-white p-1"><QRCodeSVG value={typeof window !== "undefined" ? window.location.origin : "https://zorschool.com"} size={52} /></div>
        </div>
      </div>

      {/* Puan halkaları + dereceler + geçmiş */}
      <div className="mt-3 grid gap-3 sm:grid-cols-[1.5fr_1fr_1fr] print:grid-cols-[1.5fr_1fr_1fr]">
        <div className="flex min-w-0 flex-nowrap items-start justify-around gap-2 overflow-hidden rounded-xl border border-slate-200 bg-white p-3 break-inside-avoid">
          {(d.circles || []).map((c) => <Circle key={c.label} label={c.label} value={c.value} max={d.maxNet} color={c.color} />)}
        </div>
        <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 break-inside-avoid">
          <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">Katılım ve Dereceler</p>
          <table className="w-full text-xs">
            <thead><tr className="text-slate-400"><th className="py-1 text-left">Alan</th><th className="text-center">Katılım Sayısı</th><th className="text-center">Derece</th></tr></thead>
            <tbody>
              {(d.ranks || []).map((r) => (
                <tr key={r.label} className="border-t border-slate-100"><td className="py-1 font-bold">{r.label}</td><td className="text-center">{fmt(r.count)}</td><td className="text-center font-extrabold text-[#16324F]">{fmt(r.rank)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-3 break-inside-avoid">
          <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">Geçmiş Sınav Netleriniz</p>
          {(!d.history || d.history.length === 0) ? <p className="py-6 text-center text-xs text-slate-400">İlk sınavınız</p> : (
            <div className="flex h-24 items-end justify-center gap-1.5">
              {d.history.slice(-8).map((h, i, arr) => (
                <div key={i} className="flex flex-col items-center gap-0.5">
                  <span className="text-[9px] font-bold text-slate-500">{h.net}</span>
                  <div className={`w-5 rounded-t ${i === arr.length - 1 ? "bg-[#16324F]" : "bg-slate-300"}`} style={{ height: `${d.maxNet ? Math.max(6, (h.net / d.maxNet) * 72) : 6}px` }} />
                  <span className="max-w-11 truncate text-[8px] text-slate-400">{h.label.split(" ")[0]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* LGS puanı */}
      {d.puan && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 break-inside-avoid" data-testid="lgs-puan-box">
          {d.puan.puan != null ? (
            <div className="flex flex-wrap items-start gap-4">
              <Circle label="LGS Puanı (Maks: 500)" value={d.puan.puan} max={500} color="#16324F" />
              <div className="min-w-52 flex-1">
                <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{d.puan.label}</span>
                <table className="mt-1.5 w-full max-w-md text-[11px]">
                  <tbody>
                    <tr className="border-t border-slate-100"><td className="py-0.5 text-slate-500">TASP (Ağırlıklı Standart Puan Toplamı)</td><td className="text-right font-bold">{d.puan.tasp}</td></tr>
                    <tr className="border-t border-slate-100"><td className="py-0.5 text-slate-500">Min / Maks TASP</td><td className="text-right font-bold">{d.puan.min_tasp} / {d.puan.max_tasp}</td></tr>
                    <tr className="border-t border-slate-100"><td className="py-0.5 text-slate-500">Veri Kaynağı</td><td className="text-right font-bold">{d.puan.kaynak}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="w-full overflow-x-auto">
                <table className="mt-1 w-full text-[10px]">
                  <thead><tr className="text-slate-400"><th className="py-0.5 text-left">Ders</th><th className="text-center">Ham Puan (D−Y/3)</th><th className="text-center">Ortalama</th><th className="text-center">Std. Sapma</th><th className="text-center">Standart Puan</th><th className="text-center">Katsayı</th><th className="text-center">ASP</th></tr></thead>
                  <tbody>
                    {d.puan.subjects.map((s) => (
                      <tr key={s.name} className="border-t border-slate-50 text-center">
                        <td className="py-0.5 text-left font-bold">{s.name}</td><td>{s.hp}</td><td>{fmt(s.ort)}</td><td>{fmt(s.std)}</td><td>{fmt(s.sp)}</td><td>{s.katsayi}</td><td className="font-bold text-[#16324F]">{fmt(s.asp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">Puan notu: {d.puan.note}</p>
          )}
        </div>
      )}

      {/* Dersler tablosu */}
      <table className="mt-3 w-full text-xs break-inside-avoid">
        <thead><tr className="bg-[#16324F] text-white">
          <th className="rounded-l-lg px-2 py-1.5 text-left">Dersler</th><th>Soru Sayısı</th><th>Doğru</th><th>Yanlış</th><th>Boş</th><th>Net</th><th>Sınıf Sırası</th><th>Başarı Yüzdesi</th><th className="rounded-r-lg">Sınıf Başarı Yüzdesi</th>
        </tr></thead>
        <tbody>
          {(d.subjects || []).map((s) => (
            <tr key={s.name} className="border-t border-slate-100 text-center">
              <td className="px-2 py-1.5 text-left font-bold">{s.name}</td><td>{s.soru}</td><td className="font-bold text-[#16324F]">{s.dogru}</td><td className="text-rose-500">{s.yanlis}</td><td className="text-slate-400">{s.bos}</td><td className="font-extrabold text-[#16324F]">{s.net}</td><td>{fmt(s.rank)}</td><td className={weak(s.basari) ? "font-bold text-rose-500" : ""}>{s.basari}</td><td>{fmt(s.class_pct)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300 text-center font-extrabold">
            <td className="px-2 py-1.5 text-left">Toplam</td><td>{(d.subjects || []).reduce((a, s) => a + (s.soru || 0), 0)}</td><td className="text-[#16324F]">{d.total.dogru}</td><td className="text-rose-500">{d.total.yanlis}</td><td className="text-slate-400">{d.total.bos}</td><td className="text-[#16324F]">{d.total.net}</td><td></td><td data-testid="karne-total-basari">{(() => { const q = (d.subjects || []).reduce((a, s) => a + (s.soru || 0), 0); return q ? Math.round((d.total.net / q) * 100) : 0; })()}</td><td></td>
          </tr>
        </tbody>
      </table>

      {/* Optik cevap tablosu */}
      <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 break-inside-avoid">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Optik Cevap Tablosu <span className="font-normal normal-case">(yeşil = doğru, kırmızı = yanlış, boş = işaretsiz)</span></p>
        {(d.subjects || []).map((s) => (
          <div key={s.name} className="mb-2 overflow-x-auto">
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left font-extrabold uppercase text-white" style={{ background: subjColor(s.name) }}>{s.name}</th>
                  {(s.detay || []).map((q) => <th key={q.no} className="min-w-6 bg-[#16324F] px-1 py-1 text-center font-bold text-white">{q.no}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="bg-slate-50 px-2 py-1 text-left font-bold text-slate-500">Cevap Anahtarı</td>
                  {(s.detay || []).map((q) => <td key={q.no} className="border border-slate-100 text-center font-bold text-slate-600">{q.dogru_cevap || "—"}</td>)}
                </tr>
                <tr>
                  <td className="bg-slate-50 px-2 py-1 text-left font-bold text-slate-500">Öğrenci Cevapları</td>
                  {(s.detay || []).map((q) => (
                    <td key={q.no} className={`border border-slate-100 text-center font-bold ${q.sonuc === "dogru" ? "bg-[#16324F] text-white" : q.sonuc === "yanlis" ? "bg-rose-500 text-white" : "text-slate-300"}`}>{q.secilen || ""}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Kazanım değerlendirme tablosu */}
      <div className="mt-3 break-inside-avoid">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Kazanım Değerlendirme Tablosu <span className="font-normal normal-case">(rozetler = soru numaraları; yeşil doğru, kırmızı yanlış)</span></p>
        <div className="grid gap-3 sm:grid-cols-2">
          {(d.subjects || []).filter((s) => (s.topics && s.topics.length) || (s.extra && s.extra.length) || d.onExtraAdd).map((s) => {
            const qmap = {};
            (s.detay || []).forEach((q) => { const k = q.kazanim || "Genel"; (qmap[k] = qmap[k] || []).push(q); });
            return (
              <div key={s.name} className="overflow-hidden rounded-xl border border-slate-200 bg-white break-inside-avoid">
                <div className="px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wide text-white" style={{ background: subjColor(s.name) }}>{s.name}</div>
                <table className="w-full text-[11px]">
                  <thead><tr className="bg-slate-50 text-slate-400"><th className="px-2 py-1 text-left">Kazanım</th><th className="px-1 py-1 text-center">Sorular</th><th className="w-8 px-1 text-center">SS</th><th className="w-14 px-1 text-center">Başarı (%)</th><th className="w-14 px-1 text-center">Genel (%)</th></tr></thead>
                  <tbody>
                    {s.topics.map((t, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${weak(t.basari) ? "bg-rose-50" : ""}`}>
                        <td className={`px-2 py-1 ${weak(t.basari) ? "font-bold text-rose-600" : "text-slate-600"}`}>{t.kazanim}</td>
                        <td className="px-1 py-1 text-center">
                          <span className="inline-flex flex-wrap justify-center gap-0.5">
                            {(qmap[t.kazanim] || []).map((q) => (
                              <span key={q.no} className={`flex h-4 w-4 items-center justify-center rounded text-[8px] font-bold ${q.sonuc === "dogru" ? "bg-[#16324F] text-white" : q.sonuc === "yanlis" ? "bg-rose-500 text-white" : "bg-slate-200 text-slate-400"}`}>{q.no}</span>
                            ))}
                          </span>
                        </td>
                        <td className="text-center">{t.ss}</td>
                        <td className={`text-center font-bold ${weak(t.basari) ? "text-rose-600" : "text-slate-700"}`}>{t.basari}</td>
                        <td className="text-center text-slate-500">{t.genel}</td>
                      </tr>
                    ))}
                    {(s.extra || []).map((x, xi) => (
                      <tr key={`extra-${xi}`} className="border-t border-slate-100 bg-slate-50">
                        <td className="px-2 py-1 text-slate-700">
                          {x} <span className="ml-1 rounded bg-slate-200 px-1 py-0.5 text-[9px] font-bold text-slate-600">ELLE EKLENDİ</span>
                          {d.onExtraRemove && <button data-testid={`extra-kz-del-${s.name}-${xi}`} onClick={() => d.onExtraRemove(s.name, x)} className="no-print ml-1.5 inline-flex align-middle text-rose-400 transition hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>}
                        </td>
                        <td className="px-1 py-1 text-center text-slate-300">—</td>
                        <td className="text-center text-slate-300">—</td>
                        <td className="text-center text-slate-300">—</td>
                        <td className="text-center text-slate-300">—</td>
                      </tr>
                    ))}
                    {d.onExtraAdd && (
                      <tr className="no-print border-t border-dashed border-slate-200">
                        <td colSpan={5} className="px-2 py-1.5"><ExtraInput subjectName={s.name} onAdd={(txt) => d.onExtraAdd(s.name, txt)} /></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>

      <p className="mt-3 text-[10px] italic text-slate-400">- Kırmızı işaretli konular, başarı yüzdesi %50'nin altında kalan ve öncelikli tekrar edilmesi önerilen kazanımlardır. — Sonuçlarınıza ve geçmiş karnelerinize telefonunuzdan QR kodu okutarak sistemimizden ulaşabilirsiniz.</p>
    </div>
  );
}
